import { interfaceError } from '../../i18n/errorMessages';
import toast from '../../i18n/toast';
import React, {useEffect, useMemo, useState} from "react";
import {connect} from "react-redux";
import * as PropTypes from "prop-types";
import 'react-toastify/dist/ReactToastify.css';
import { useLocation } from "react-router-dom";
import ContactDetailsFilter from './ContactDetailsFilter';
import { matchesContactFilter, normalizeContactFilter, normalizeNotificationFilter } from './contactFilterRules';
import { history } from "../../redux/store/configureStore";
import ClientsList from "./ClientsList";
import { getEffectivePermissionRole } from "./rbac";
import * as clientActions from "../../redux/actions/clientActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as settingsActions from "../../redux/actions/settingsActions";
import {filterArray, getStudentsByClientId} from "../common/Helpers";
import {
  buildManageRouteWithSchoolYear,
  getNormalizedSchoolYearStartYearFromSearch,
} from "./manageSchoolYearRouting";
import { useTranslation } from 'react-i18next';

export function Clients({
  clients,
  loadClients,
  showArchivedClients,
  toggleClientList,
  students,
  loadStudents,
  peopleViewState,
  onPeopleViewStateChange,
  readOnly,
}) {
  const { t: tr } = useTranslation();
  const [contactFilter, setContactFilter] = useState(normalizeContactFilter(peopleViewState && peopleViewState.contactFilter));
  const [notificationFilter, setNotificationFilter] = useState(normalizeNotificationFilter(peopleViewState && peopleViewState.notificationFilter));
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const isPeopleViewStateControlled = Boolean(peopleViewState);
  const [search, setSearch] = useState((peopleViewState && peopleViewState.search) || '');
  const [showArchivedClientsLocal, setShowArchivedClientsLocal] = useState(Boolean(peopleViewState && peopleViewState.showArchived));
  const [clientsEx, setClientsEx] = useState([]);
  const effectiveShowArchivedClients = isPeopleViewStateControlled ? showArchivedClientsLocal : showArchivedClients;
  const clientsForSelectedArchive = useMemo(() => clients.filter(client => (
    effectiveShowArchivedClients ? client.archived : !client.archived
  )), [clients, effectiveShowArchivedClients]);

  useEffect(() => {
    if (clients.length === 0) {
      loadClients()
        .catch((error) => {
          toast.error(interfaceError(error, 'common:errors.load'));
        });
    }

    if (students.length === 0) {
      loadStudents()
        .catch((error) => {
          toast.error(interfaceError(error, 'common:errors.load'));
        });
    }
  }, []);

  useEffect(() => {
    setClientsEx(clientsForSelectedArchive.map(client => ({...client, expanded: false, students: getStudentsByClientId(students, client.id)})));
  }, [clientsForSelectedArchive, students]);

  const handleEdit = (client) => {
    history.push(buildManageRouteWithSchoolYear(`/manage/client/${client.id}`, location.search, activeSchoolYearStartYear));
  };

  const handleExpand = (client) => {
    setClientsEx(clientsEx.map(c => (c.id === client.id ? {...client, expanded: !client.expanded} : c)));
  };

  const handleAdd = () => {
    history.push(buildManageRouteWithSchoolYear("/manage/client/new", location.search, activeSchoolYearStartYear));
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearch(value);
    onPeopleViewStateChange({ search: value });
  };

  const filteredClients = filterArray(search, clientsEx).filter(person => (
    matchesContactFilter(person, contactFilter) &&
    (notificationFilter === 'all' || (person.sendNotifications === true) === (notificationFilter === 'enabled'))
  ));

  const handleArchivedClients = () => {
    if (isPeopleViewStateControlled) {
      const nextValue = !showArchivedClientsLocal;
      setShowArchivedClientsLocal(nextValue);
      onPeopleViewStateChange({ showArchived: nextValue });
      return;
    }
    toggleClientList();
  };

  const handleContactFilter = (value, notifications = notificationFilter) => {
    setContactFilter(value);
    onPeopleViewStateChange({ contactFilter: value });
    if (notifications !== notificationFilter) {
      setNotificationFilter(notifications);
      onPeopleViewStateChange({ notificationFilter: notifications });
    }
  };
  const clearFilters = () => {
    setSearch('');
    setContactFilter('all');
    setNotificationFilter('all');
    setShowArchivedClientsLocal(false);
    if (!isPeopleViewStateControlled && showArchivedClients) toggleClientList();
    onPeopleViewStateChange({ search: '', contactFilter: 'all', notificationFilter: 'all', showArchived: false });
  };

  return (
    <div className="mb-5">
      <h2 className="manage-title col-block mb-0">{tr("manage:clientList_152")}</h2>
      <div className="contact-toolbar">
        <input name="search" className="form-control contact-search" value={search} onChange={handleSearchChange} placeholder={tr("manage:search_153")}/>
        <ContactDetailsFilter value={contactFilter} notificationFilter={notificationFilter} onChange={handleContactFilter} onClear={clearFilters} empty={filteredClients.length === 0} />
        <div>
          <label className="contact-archive"><input type="checkbox" checked={effectiveShowArchivedClients} onChange={handleArchivedClients} />{tr("manage:archive_154")}</label>
        </div>
        <div className="contact-add">
          {!readOnly && !effectiveShowArchivedClients && (
            <button
              type="button"
              className="btn manage-button btn-success"
              onClick={handleAdd}
            >{tr("manage:addANewClient_155")}</button>
          )}
        </div>
      </div>
      <ClientsList
        clients={filteredClients}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onExpand={handleExpand}
        showArchivedClients={effectiveShowArchivedClients}
        initialNumToShow={peopleViewState ? peopleViewState.numToShow : 25}
        onNumToShowChange={numToShow => onPeopleViewStateChange({ numToShow })}
      />
    </div>
  );
}


Clients.propTypes = {
  readOnly: PropTypes.bool,
  clients: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  loadStudents: PropTypes.func.isRequired,
  showArchivedClients: PropTypes.bool.isRequired,
  toggleClientList: PropTypes.func.isRequired,
  peopleViewState: PropTypes.shape({
    search: PropTypes.string,
    showArchived: PropTypes.bool,
    numToShow: PropTypes.number,
    contactFilter: PropTypes.string,
    notificationFilter: PropTypes.string,
  }),
  onPeopleViewStateChange: PropTypes.func,
};

Clients.defaultProps = {
  readOnly: false,
  peopleViewState: null,
  onPeopleViewStateChange: () => {},
};

const mapStateToProps = (state) => {
  const showArchivedClients = state.settings.showArchivedClients;

  return ({
    readOnly: getEffectivePermissionRole(state.user) === "TEACHER",
    clients: state.clients.filter(client => !client.deleted),
    showArchivedClients,
    students: state.students,
  });
};

const mapDispatchToProps = dispatch => ({
  loadClients: () => dispatch(clientActions.loadClients()),
  loadStudents: () => dispatch(studentActions.loadStudents()),
  toggleClientList: () => dispatch(settingsActions.toggleClientList()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Clients);
